import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { AppShell } from '@/components/app/AppShell';
import { InlineAlert } from '@/components/app/Modal';
import { EmptyState } from '@/components/app/EmptyState';
import { AskAboutButton } from '@/components/app/AskAboutButton';
import { HelpHint } from '@/components/app/HelpHint';
import { ExampleChips } from '@/components/app/ExampleChips';
import { FeedbackButtons } from '@/components/app/FeedbackButtons';
import { searchApi, SearchResult } from '@/lib/search';
import { readApiError } from '@/lib/auth';

/**
 * Raw semantic-search results — runs the hybrid retrieval pipeline (Qdrant
 * dense + lexical + graph, RRF-fused + reranked) and shows ranked chunks with
 * their score + which lanes surfaced them, deep-linked to the source document.
 * Distinct from Ask (which feeds these to an LLM for a written answer).
 */
export function SearchPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('seed') ?? '');
  const seedConsumed = useRef(false);

  const run = useMutation({
    mutationFn: (q: string) => searchApi.search(wsId!, q, 12),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (q) run.mutate(q);
  };

  // Inbound ?seed= (from Docs/DocViewer/Audit) prefills and runs once.
  useEffect(() => {
    const seed = searchParams.get('seed');
    if (seed && !seedConsumed.current) {
      seedConsumed.current = true;
      setQuery(seed);
      run.mutate(seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <AppShell
      title={t('search.title', { defaultValue: 'Search' })}
      description={t('search.subtitle', { defaultValue: 'Semantic + keyword search across your indexed knowledge. Ranked passages with source links.' }) as string}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-5">
        <HelpHint page="search" />
        <form onSubmit={onSubmit} className="flex gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('search.placeholder', { defaultValue: 'Search documents, meetings, contracts…' }) as string}
            className="flex-1 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
          <button
            type="submit"
            disabled={!query.trim() || run.isPending}
            className="btn-press px-4 py-2 text-sm disabled:opacity-40"
          >
            {run.isPending ? t('search.searching', { defaultValue: 'Searching…' }) : t('search.submit', { defaultValue: 'Search' })}
          </button>
        </form>

        {!run.data && !query.trim() && (
          <ExampleChips page="search" onPick={setQuery} />
        )}

        {query.trim() && (
          <div className="flex items-center gap-3 text-xs">
            <AskAboutButton
              wsId={wsId!}
              seed={query.trim()}
              label={t('search.ask_instead', { defaultValue: 'Ask this as a question instead →' }) as string}
            />
          </div>
        )}

        {run.isError && <InlineAlert tone="error">{readApiError(run.error, 'Search failed.')}</InlineAlert>}
        {run.data && run.data.results.length === 0 && (
          <EmptyState
            title={t('search.no_results_title', { defaultValue: 'No matches' }) as string}
            description={t('search.no_results_sub', { defaultValue: 'Try different terms, ask it as a question, or index more documents.' }) as string}
            actions={[
              { label: t('search.upload', { defaultValue: 'Upload documents' }) as string, to: `/app/workspace/${wsId}/upload`, primary: true },
              { label: t('search.connect', { defaultValue: 'Connect a source' }) as string, to: `/app/workspace/${wsId}/connectors` },
              { label: t('search.try_ask', { defaultValue: 'Ask instead' }) as string, to: `/app/workspace/${wsId}/ask?seed=${encodeURIComponent(query.trim())}` },
            ]}
          />
        )}

        <div className="space-y-3">
          {run.data?.results.map((r) => <ResultCard key={r.chunkId} r={r} wsId={wsId!} query={run.variables} />)}
        </div>
      </div>
    </AppShell>
  );
}

const LANE_LABEL: Record<string, string> = { dense: 'semantic', lexical: 'keyword', graph: 'graph' };

function ResultCard({ r, wsId, query }: { r: SearchResult; wsId: string; query?: string }) {
  const { t } = useTranslation();
  return (
    <article className="card-paper p-4">
      <div className="flex items-start justify-between gap-3">
        <Link to={`/app/workspace/${wsId}/docs/${r.documentId}`} className="font-medium text-ink-900 hover:underline truncate">
          {r.documentTitle}
        </Link>
        <div className="flex items-center gap-1.5 shrink-0 text-[10px]">
          {r.lanes.map((l) => (
            <span key={l} className="chip-citation uppercase">{LANE_LABEL[l] ?? l}</span>
          ))}
          <span className="num text-ink-400">{r.score.toFixed(3)}</span>
        </div>
      </div>
      {r.headingPath.length > 0 && (
        <div className="mt-0.5 text-[11px] text-ink-400 truncate">{r.headingPath.join(' › ')}{r.page ? ` · p.${r.page}` : ''}</div>
      )}
      <p className="mt-2 text-sm text-ink-700 whitespace-pre-wrap">{r.snippet}</p>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="text-[11px] text-ink-400">{r.sourceKind}{r.sourceUrl ? ' · ' : ''}{r.sourceUrl && (
          <a href={r.sourceUrl} target="_blank" rel="noreferrer" className="text-accent-600 hover:underline">{t('search.open_source', { defaultValue: 'open source ↗' })}</a>
        )}</div>
        <div className="flex items-center gap-3 shrink-0">
          <FeedbackButtons wsId={wsId} kind="search" targetId={r.chunkId} query={query} />
          <AskAboutButton wsId={wsId} seed={r.documentTitle} label={t('search.ask_about', { defaultValue: 'Ask about this' }) as string} />
          <Link to={`/app/workspace/${wsId}/docs/${r.documentId}`} className="text-[12px] text-accent-600 hover:underline">
            {t('search.view_doc', { defaultValue: 'View document' })}
          </Link>
        </div>
      </div>
    </article>
  );
}
